import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft, Star } from "lucide-react";
import { DAYS, colorOf, fmtTime, todayKey, sessionsForDay, dayName, dayShort, GOLD } from "../../lib/content";
import { edge } from "./WeekGrid";
import { useLang } from "../../lib/i18n";

const Card = ({ cls, s, i, onOpen }) => {
  const spec = cls.program !== "dillusions";
  const c = spec ? GOLD : colorOf(cls.color);
  const { t, lang, pick } = useLang();
  const Chev = lang === "ar" ? ChevronLeft : ChevronRight;
  return (
    <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      onClick={() => onOpen(cls)} data-testid={`schedule-class-card-${cls.id}-${s.day.toLowerCase()}-mobile`}
      className={`flex w-full items-stretch gap-4 rounded-xl p-4 text-start active:scale-[0.99] ${spec ? "border-[1.5px] bg-[#14120B]" : "border border-white/10 bg-[#12121A]"}`}
      style={spec ? { borderColor: GOLD } : { boxShadow: edge(lang, c), background: `${c}1F` }}>
      <div className="w-[76px] shrink-0">
        <span className="block font-mono text-[13px] font-medium" style={{ color: c }}>{s.start ? fmtTime(s.start, true, lang) : t.tba}</span>
        <span className="block font-mono text-[11px] text-zinc-400">{s.end ? `${t.to} ${fmtTime(s.end, true, lang)}` : s.start ? "" : t.time}</span>
      </div>
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 font-display text-lg font-bold leading-tight text-white">{spec && <Star size={14} fill={GOLD} strokeWidth={0} className="shrink-0" />}{pick(cls, "title")}</span>
        <span className="block text-sm text-zinc-300">{cls.program === "dillusions" ? `${pick(cls, "tag")} · ${pick(cls, "style")}` : [pick(cls, "style"), pick(cls, "ages")].filter(Boolean).join(" · ")}</span>
      </div>
      <Chev size={18} className="self-center text-zinc-500" />
    </motion.button>
  );
};

export const DayList = ({ classes, onOpen }) => {
  const today = todayKey();
  const { t, lang, pick } = useLang();
  const [day, setDay] = useState(today);
  const list = sessionsForDay(classes, day);
  const unscheduled = classes.filter((c) => c.sessions.length === 0);
  const count = (d) => sessionsForDay(classes, d).length;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101017]/90 p-4 backdrop-blur-xl" data-testid="day-list">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="font-display text-lg font-bold uppercase text-white">{t.weekly}</h2>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{dayName(day, lang)}</span>
      </div>
      <div className="-mx-1 grid grid-cols-7 gap-1" role="tablist">
        {DAYS.map((d) => (
          <button key={d} onClick={() => setDay(d)} role="tab" aria-selected={day === d} data-testid={`schedule-day-tab-${d.toLowerCase()}`}
            className={`relative flex flex-col items-center rounded-xl py-2.5 transition-colors ${day === d ? "bg-accent text-onaccent" : "bg-white/[0.04] text-zinc-300"}`}>
            <span className="font-mono text-[11px] font-bold uppercase">{dayShort(d, lang)}</span>
            <span className={`mt-1 h-1.5 w-1.5 rounded-full ${count(d) ? (day === d ? "bg-black" : "bg-accent2") : "bg-transparent"}`} />
            {d === today && <span className={`absolute -top-1.5 rounded-full px-1 font-mono text-[8px] font-bold uppercase ${day === d ? "bg-black text-accent" : "bg-accent text-onaccent"}`}>{t.today}</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2.5" key={day}>
        {list.length ? list.map(({ cls, s }, i) => <Card key={`${cls.id}-${i}`} cls={cls} s={s} i={i} onOpen={onOpen} />) : (
          <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center" data-testid="day-list-empty">
            <p className="font-display text-lg font-bold text-white">{t.restDay}</p>
            <p className="mt-1 text-sm text-zinc-400">{t.noClasses(dayName(day, lang))}</p>
          </div>
        )}
      </div>
      {unscheduled.length > 0 && (
        <p className="mt-4 text-sm text-zinc-400" data-testid="day-list-unscheduled">
          {t.comingSoon}: {unscheduled.map((c) => t.withName(pick(c, "style"), pick(c, "title"))).join("، ")} — {t.scheduleTba}.
        </p>
      )}
    </div>
  );
};
